// Typography roles + curated Google Fonts + CSS generator for the public site.
export const FONT_ROLES = ['heading', 'body', 'menu', 'button', 'hero_title', 'hero_text'];
export const FONT_BLANK = { family: '', weight: '', scale: 100, tracking: null };

export const ROLE_META = {
  heading: { title: 'Títulos', hint: 'H1/H2/H3 das seções, cards e páginas (e citações em serifa)', current: 'Fraunces', sample: 'Nossas Peregrinações' },
  body: { title: 'Textos', hint: 'Parágrafos e corpo do site (tamanho afeta parágrafos)', current: 'Inter', sample: 'Uma experiência espiritual inesquecível, com fé e conforto.' },
  menu: { title: 'Menu', hint: 'Abas do menu do topo', current: 'Inter', sample: 'PEREGRINAÇÕES · GALERIA · CONTATO' },
  button: { title: 'Botões', hint: 'Botões e links em forma de botão', current: 'Inter', sample: 'QUERO A MINHA VIAGEM' },
  hero_title: { title: 'Hero — título', hint: 'Frase grande do banner principal', current: 'Fraunces', sample: 'Peregrinar com fé' },
  hero_text: { title: 'Hero — subtítulo', hint: 'Boas-vindas e frases do banner', current: 'Inter', sample: 'Embarque em nossas viagens de Fé' },
};

export const CURATED_FONTS = [
  ...['Fraunces', 'Playfair Display', 'Cormorant Garamond', 'Cormorant', 'Lora', 'Spectral', 'Bodoni Moda', 'Libre Baskerville', 'EB Garamond',
    'Cinzel', 'Crimson Pro', 'Merriweather', 'DM Serif Display', 'Marcellus', 'Prata'].map(f => ({ family: f, cat: 'serif' })),
  ...['Inter', 'Manrope', 'Work Sans', 'Montserrat', 'Lato', 'Raleway', 'Poppins', 'Nunito Sans', 'Source Sans 3', 'Josefin Sans',
    'DM Sans', 'Jost', 'Outfit'].map(f => ({ family: f, cat: 'sans-serif' })),
  ...['Great Vibes', 'Parisienne', 'Allura', 'Pinyon Script', 'Italianno'].map(f => ({ family: f, cat: 'cursive' })),
];
export const CAT_LABEL = { serif: 'Serifadas', 'sans-serif': 'Sem serifa', cursive: 'Manuscritas' };

const catOf = (family) => CURATED_FONTS.find(f => f.family === family)?.cat || 'sans-serif';
export const fontStack = (family) => `"${family}", ${catOf(family)}`;
// CSS API v1 tolerates weights a family doesn't have (v2 would 400 the whole request).
export const fontCssUrl = (families, weights = '300,400,500,600,700,800,900,400italic') =>
  `https://fonts.googleapis.com/css?family=${families.map(f => `${f.replace(/ /g, '+')}:${weights}`).join('|')}&display=swap`;

const W = (inner) => `body.public-mode :where(${inner})`;
// Generic roles skip elements owned by a specific role (menu, hero): "Atual" there keeps today's look.
const free = (list) => list.split(',').map(x => `${x.trim()}:not([data-font])`).join(', ');
const HEADINGS = 'h1, h2, h3, h4, h5, h6';
const BUTTONS = 'button, [role="button"], a[class*="px-"][class*="py-"]';
// Later roles win (same specificity, all !important). Elements styled inline with
// Inter/Fraunces are matched by attribute so the override reaches the whole site.
const TARGETS = {
  body: { family: `body.public-mode, ${W(free('p, li, label, input, textarea, select, td, th, blockquote, small, [style*="Inter"]'))}`, props: W(free('p, li')), zoom: W(free('p')) },
  heading: { family: W(free(`${HEADINGS}, [style*="Fraunces"]`)), props: W(free(HEADINGS)), zoom: W(free(HEADINGS)) },
  button: { family: W(free(BUTTONS)), props: W(free(BUTTONS)), zoom: W(free(BUTTONS)) },
  menu: { family: W('[data-font="menu"]'), props: W('[data-font="menu"]'), zoom: W('[data-font="menu"]') },
  hero_text: { family: W('[data-font="hero-text"]'), props: W('[data-font="hero-text"]'), zoom: W('[data-font="hero-text"]') },
  hero_title: { family: W('[data-font="hero-title"]'), props: W('[data-font="hero-title"]'), zoom: W('[data-font="hero-title"]') },
};
const ORDER = ['body', 'heading', 'button', 'menu', 'hero_text', 'hero_title'];

export const usedFamilies = (typo = {}) => [...new Set(FONT_ROLES.map(k => typo[k]?.family).filter(Boolean))];

const SPECIFIC = ['menu', 'hero_text', 'hero_title'];

export const buildTypographyCss = (typo = {}) => {
  const anyFamily = usedFamilies(typo).length > 0;
  return ORDER.map((k) => {
  const r = { ...FONT_BLANK, ...(typo[k] || {}) };
  const t = TARGETS[k];
  const out = [];
  // Specific roles left "Atual" are pinned to today's font so they don't inherit a new body font.
  const family = r.family || (anyFamily && SPECIFIC.includes(k) ? ROLE_META[k].current : '');
  if (family) out.push(`${t.family} { font-family: ${fontStack(family)} !important; }`);
  const props = [r.weight && `font-weight: ${r.weight} !important;`, r.tracking !== null && r.tracking !== undefined && `letter-spacing: ${r.tracking}em !important;`].filter(Boolean);
  if (props.length) out.push(`${t.props} { ${props.join(' ')} }`);
  if (Number(r.scale) && Number(r.scale) !== 100) out.push(`${t.zoom} { zoom: ${Number(r.scale) / 100}; }`);
  return out.join('\n');
  }).filter(Boolean).join('\n');
};
